/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import type { Device } from '@studio/models/device';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control, DeviceRegistryCommand } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryCommandOption,
  DeviceRegistryInterfaceItem,
  Label
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { DeviceRegistryInterfaceItemControlType } from '@tryiris-ai/common.protojs/api/v1/device_settings';

import type { Proxy } from '#~/agent/index.js';
import type { State as DeviceSettingsState } from '#~/device/settings/index.js';
import { usePrompt } from '@studio/design-system/prompt';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { calculateConditionsForItem } from '../conditions.js';
import { createReactiveMutationHandler } from '../reactive-mutation-handler.js';
import { DeviceSettingButton } from './button.jsx';
import { DeviceSettingDialIndeterminate } from './dial-indeterminate.jsx';
import { DeviceSettingDial } from './dial.jsx';
import { DeviceSettingDirectNumericEntry } from './direct-numeric-entry.js';
import { DeviceSettingSelect } from './select.jsx';
import { DeviceSettingSlider } from './slider.jsx';
import { DeviceSettingSwitch } from './switch.jsx';
import { DeviceSettingToggleGroup } from './toggle-group.jsx';

/** @internal */
export const DeviceSettingControl = ({
  agent,
  className,
  commandMap,
  defaultsMap,
  deviceId,
  deviceSettings,
  isDisabled,
  isHidden,
  item,
  onMutation,
  onControlClick
}: {
  agent: T_Maybe<Proxy>;
  className: T_Maybe<string>;
  commandMap: Record<number, DeviceRegistryCommand>;
  defaultsMap: Record<number, number>;
  deviceId: Device['id'];
  deviceSettings: DeviceSettingsState;
  isDisabled: boolean;
  isHidden: boolean;
  item: DeviceRegistryInterfaceItem;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
}): ReactElement | null => {
  const cmd = commandMap[item.key];

  const value = deviceSettings[item.key];

  // Optional prompt hook for modal confirmations (currently not used, but available for future enhancement)
  const prompt = usePrompt();

  // Create reactive mutation handler that routes between immediate and deferred mutations.
  // deviceId arms the ENG-4477 deleted-device guard: a control that fires (or
  // a modal that confirms) after this device was deleted must not mutate.
  const reactiveMutationHandler = useMemo(
    () => createReactiveMutationHandler(agent, onMutation, prompt, deviceId),
    [agent, onMutation, prompt, deviceId]
  );

  // Enhanced mutation handler that considers saveBehavior
  const handleMutation = async (mutation: DeepPartial<Control>): Promise<void> => {
    await reactiveMutationHandler.handleMutation(mutation, item);
  };

  const handleControlClick = (controlValue?: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => {
    onControlClick?.(controlValue!);
  };
  const status = value?.error && !isDisabled ? 'warning' : undefined;

  if (!cmd || isHidden) {
    return null;
  }

  switch (item.controlType) {
    case DeviceRegistryInterfaceItemControlType.SWITCH: {
      return (
        <DeviceSettingSwitch
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          deviceId={deviceId}
          disabled={isDisabled}
          label={item.label ?? ''}
          onMutation={onMutation}
          onReactiveMutation={handleMutation}
          value={value?.toggle ?? false}
          onControlClick={handleControlClick}
          status={status}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.SLIDER:
    case DeviceRegistryInterfaceItemControlType.SLIDER_BLUE:
    case DeviceRegistryInterfaceItemControlType.SLIDER_RED: {
      return (
        <DeviceSettingSlider
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          controlType={item.controlType}
          defaultValue={defaultsMap[item.key]}
          displayMax={cmd?.value?.rangeValue?.max ?? 1}
          displayMin={cmd?.value?.rangeValue?.min ?? 0}
          deviceId={deviceId}
          disabled={isDisabled}
          label={item.label ?? ''}
          onMutation={onMutation}
          onReactiveMutation={handleMutation}
          value={value?.direct ?? 0}
          onControlClick={handleControlClick}
          status={status}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.SELECT_TOGGLE_GROUP_SINGLE:
    case DeviceRegistryInterfaceItemControlType.SELECT_TOGGLE_GROUP_MULTI: {
      const items = (cmd?.value?.labels ?? cmd?.select?.options ?? []).map((item, index) => ({
        label: (item as { name: string }).name ?? (item as { label: string }).label,
        value: index
      }));

      const single =
        item.controlType === DeviceRegistryInterfaceItemControlType.SELECT_TOGGLE_GROUP_SINGLE;

      return (
        <DeviceSettingToggleGroup
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          defaultValue={defaultsMap[item.key]}
          deviceId={deviceId}
          disabled={isDisabled}
          items={items}
          label={item.label ?? ''}
          onMutation={onMutation}
          onReactiveMutation={handleMutation}
          single={single}
          value={value?.select ?? -1}
          onControlClick={handleControlClick}
          status={status}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.SELECT: {
      const rawItems = (cmd?.value?.labels ?? cmd?.select?.options ?? []) as (
        | DeviceRegistryCommandOption
        | Label
      )[];

      const items = rawItems
        .map?.((item, index) => {
          const { isDisabled: disabled, isHidden: hidden } = calculateConditionsForItem(
            item.conditions,
            deviceSettings,
            commandMap
          );

          if (hidden) {
            return undefined;
          }

          return {
            disabled,
            label: (item as { name: string }).name ?? (item as { label: string }).label,
            value: index
          };
        })
        .filter((item) => !!item) as { disabled: boolean; label: string; value: number }[];

      return (
        <DeviceSettingSelect
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          defaultValue={defaultsMap[item.key]}
          deviceId={deviceId}
          disabled={isDisabled}
          items={items}
          label={item.label ?? ''}
          onMutation={onMutation}
          onReactiveMutation={handleMutation}
          value={value?.select ?? -1}
          onControlClick={handleControlClick}
          status={status}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.DIAL: {
      const items = (cmd?.value?.labels ?? cmd?.select?.options ?? []).map((item, index) => ({
        label: (item as { name: string }).name ?? (item as { label: string }).label,
        value: (item as { value: number }).value ?? index
      }));

      return (
        <DeviceSettingDial
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          defaultValue={defaultsMap[item.key]}
          deviceId={deviceId}
          disabled={isDisabled}
          items={items}
          label={item.label ?? ''}
          onMutation={onMutation}
          value={value?.direct ?? -1}
          onControlClick={handleControlClick}
          status={status}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.BUTTON: {
      return (
        <DeviceSettingButton
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          deviceId={deviceId}
          disabled={isDisabled}
          label={item.label ?? ''}
          onMutation={onMutation}
          onReactiveMutation={handleMutation}
          onControlClick={handleControlClick}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.DIAL_INDETERMINATE:
    case DeviceRegistryInterfaceItemControlType.DIAL_INDETERMINATE_CONTINUOUS: {
      return (
        <DeviceSettingDialIndeterminate
          agent={agent}
          className={className ?? ''}
          cmdKey={item.key}
          continuous={
            item.controlType ===
            DeviceRegistryInterfaceItemControlType.DIAL_INDETERMINATE_CONTINUOUS
          }
          defaultValue={defaultsMap[item.key]}
          deviceId={deviceId}
          disabled={isDisabled}
          label={item.label ?? ''}
          onMutation={onMutation}
          value={value?.direct ?? -1}
          onControlClick={handleControlClick}
        />
      );
    }

    case DeviceRegistryInterfaceItemControlType.DIRECT_NUMERIC_ENTRY: {
      return (
        <DeviceSettingDirectNumericEntry
          agent={agent}
          className={className ?? ''}
          cmd={cmd}
          cmdKey={item.key}
          controlType={item.controlType}
          defaultValue={defaultsMap[item.key]}
          deviceId={deviceId}
          disabled={isDisabled}
          label={item.label ?? ''}
          onMutation={onMutation}
          value={value?.direct ?? 0}
          onControlClick={handleControlClick}
          status={status}
        />
      );
    }
    case DeviceRegistryInterfaceItemControlType.NOOP:
    case DeviceRegistryInterfaceItemControlType.MULTI_CONTROL:
      // Groups are rendered recursively by Controls, not by an individual widget.
      return null;
    default:
      // Check enum growth at compile time while keeping unknown wire tags a silent skip.
      item.controlType satisfies never;
      return null;
  }
};
